import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdminAuth } from '../context/AdminAuthContext'
import { authService } from '../services/api'
import { Lock, User, Eye, EyeOff, AlertCircle, ShieldCheck } from 'lucide-react'
import toast from 'react-hot-toast'

export default function AdminLogin() {
  const { login, loginWithToken } = useAdminAuth()
  const navigate  = useNavigate()

  // Step 1 — credentials
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPass, setShowPass] = useState(false)

  // Step 2 — TOTP
  const [step,     setStep]     = useState(1)
  const [totpCode, setTotpCode] = useState('')

  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')

  // ── Step 1: submit credentials ──────────────────────────────────────────────
  const handleCredentials = async (e) => {
    e.preventDefault()
    if (!username || !password) { setError('Please enter your username and password'); return }
    setError('')
    setLoading(true)
    try {
      const result = await login(username, password)
      if (result.requires2fa) {
        // Server confirmed credentials valid but 2FA is required
        setStep(2)
        setTotpCode('')
      } else {
        toast.success('Welcome back!')
        navigate('/dashboard')
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Invalid credentials or insufficient privileges')
    } finally {
      setLoading(false)
    }
  }

  // ── Step 2: submit TOTP code ────────────────────────────────────────────────
  const handleTotp = async (e) => {
    e.preventDefault()
    const clean = totpCode.replace(/\s/g, '')
    if (clean.length !== 6 || !/^\d+$/.test(clean)) {
      setError('Please enter the 6-digit code from your authenticator app')
      return
    }
    setError('')
    setLoading(true)
    try {
      const res  = await authService.login2fa(username, password, clean)
      await loginWithToken(res.access_token)
      toast.success('Welcome back!')
      navigate('/dashboard')
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid authenticator code')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ backgroundColor: '#0D0D0D' }}>

      {/* Layered background */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `
          radial-gradient(ellipse 70% 40% at 5% 0%,   rgba(14,148,122,0.12) 0%, transparent 55%),
          radial-gradient(ellipse 50% 50% at 95% 100%, rgba(212,175,55,0.06)  0%, transparent 55%)
        `,
      }}/>
      <div className="absolute inset-0 pointer-events-none" style={{
        opacity: 0.022,
        backgroundRepeat: 'repeat',
        backgroundSize: '200px 200px',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Cg transform='translate(16,10) rotate(-8,18,52)' opacity='0.9'%3E%3Crect x='15' y='2' width='5' height='9' rx='2' fill='%2314b8a6'/%3E%3Crect x='13' y='11' width='9' height='4' rx='1' fill='%2314b8a6'/%3E%3Cellipse cx='18' cy='23' rx='9' ry='4' fill='%2314b8a6'/%3E%3Crect x='9' y='23' width='18' height='36' rx='3' fill='%2314b8a6'/%3E%3Cellipse cx='18' cy='59' rx='9' ry='3' fill='%2314b8a6'/%3E%3Crect x='12' y='38' width='12' height='7' rx='2' fill='%230D0D0D' opacity='0.35'/%3E%3C/g%3E%3Cg transform='translate(130,120) rotate(6,18,38)' opacity='0.8'%3E%3Cellipse cx='18' cy='8' rx='12' ry='8' fill='%2314b8a6'/%3E%3Cpath d='M7 8 Q5 24 12 30 L12 40 L24 40 L24 30 Q31 24 29 8 Z' fill='%2314b8a6'/%3E%3Crect x='16' y='40' width='4' height='12' fill='%2314b8a6'/%3E%3Crect x='8' y='50' width='18' height='3' rx='1.5' fill='%2314b8a6'/%3E%3C/g%3E%3Cg transform='translate(82,74) rotate(-4,14,18)' opacity='0.65'%3E%3Cpath d='M3 3 L25 3 L21 24 L7 24 Z' fill='%2314b8a6'/%3E%3Crect x='7' y='24' width='14' height='3' rx='1.5' fill='%2314b8a6'/%3E%3C/g%3E%3Cg transform='translate(154,12)' opacity='0.6'%3E%3Cpolygon points='12,0 24,9 12,20 0,9' fill='none' stroke='%2314b8a6' stroke-width='1.5'/%3E%3Cpolygon points='12,4 20,9 12,16 4,9' fill='none' stroke='%23FFD700' stroke-width='0.8'/%3E%3C/g%3E%3C/svg%3E")`,
      }}/>

      {/* Card */}
      <div className="relative z-10 w-full max-w-sm">

        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-block">
            <img
              src="/logo-tall.webp"
              alt="Tony's Liquor Distributor"
              className="h-48 w-auto mx-auto object-contain"
              style={{ mixBlendMode: 'multiply', filter: 'brightness(1.08) contrast(1.05)' }}
            />
          </div>
          <p className="text-gold/70 font-display text-xs tracking-[0.3em] uppercase mt-1">Admin Control Center</p>
        </div>

        <div className="bg-surface-card border border-surface-border rounded-2xl p-6 shadow-2xl backdrop-blur">

          {/* ── Step 1: Credentials ── */}
          {step === 1 && (
            <form onSubmit={handleCredentials} className="space-y-4">
              <div>
                <label className="text-xs text-gray-400 mb-1.5 block font-display uppercase tracking-wider">Username</label>
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    className="input-field pl-9"
                    type="text"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    placeholder="admin"
                    autoComplete="username"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-400 mb-1.5 block font-display uppercase tracking-wider">Password</label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    className="input-field pl-9 pr-10"
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                  >
                    {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              {error && <ErrorBanner msg={error} />}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center py-2.5 mt-2"
              >
                {loading
                  ? <><Spinner /> Authenticating...</>
                  : <><Lock size={14} /> Sign In to Admin</>}
              </button>
            </form>
          )}

          {/* ── Step 2: TOTP ── */}
          {step === 2 && (
            <form onSubmit={handleTotp} className="space-y-4">
              <div className="text-center pb-2">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 border border-primary/30 mb-3">
                  <ShieldCheck size={22} className="text-primary" />
                </div>
                <h2 className="text-white font-display font-semibold text-base">Two-Factor Authentication</h2>
                <p className="text-gray-400 text-xs mt-1.5 leading-relaxed">
                  Open your authenticator app and enter the 6-digit code for{' '}
                  <span className="text-white font-medium">Tony's Liquor Admin</span>.
                </p>
              </div>

              <div>
                <label className="text-xs text-gray-400 mb-1.5 block font-display uppercase tracking-wider">Authenticator Code</label>
                <input
                  className="input-field text-center text-2xl tracking-[0.4em] font-mono"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  autoComplete="one-time-code"
                  autoFocus
                />
              </div>

              {error && <ErrorBanner msg={error} />}

              <button
                type="submit"
                disabled={loading || totpCode.length < 6}
                className="btn-primary w-full justify-center py-2.5"
              >
                {loading
                  ? <><Spinner /> Verifying...</>
                  : <><ShieldCheck size={14} /> Verify &amp; Sign In</>}
              </button>

              <button
                type="button"
                onClick={() => { setStep(1); setError(''); setTotpCode('') }}
                className="w-full text-center text-xs text-gray-500 hover:text-gray-300 transition-colors pt-1"
              >
                ← Back to login
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-gray-600 mt-5">
          Authorised access only · Tony's Liquor Distributor
        </p>
      </div>
    </div>
  )
}

function ErrorBanner({ msg }) {
  return (
    <div className="flex items-center gap-2 bg-red-900/20 border border-red-900/40 rounded-lg px-3 py-2">
      <AlertCircle size={14} className="text-red-400 shrink-0" />
      <span className="text-red-300 text-xs">{msg}</span>
    </div>
  )
}

function Spinner() {
  return <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
}

